import { AlertTriangle, Filter, GripVertical, Loader2, Lock, MousePointerClick } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type DragEvent, type MouseEvent as ReactMouseEvent } from "react";

import { PREVIEW_SKELETON_BG, PREVIEW_STAGE_BG, type PreviewTheme } from "../shell/TvFrameTheme";
import { specFor } from "./catalog";
import { EmptyCanvas } from "./EmptyCanvas";
import { GRID, collides, isBarWidget, rowsFor, type SLayout, type SPage, type SWidget } from "./model";
import { guidesFor, snapDelta, type Guide } from "./snap";
import { stackRanks } from "./zorder";

export interface CanvasHandle {
  post: (msg: Record<string, unknown>) => void;
}

type Handle = "move" | "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";
interface Drag {
  id: string;
  handle: Handle;
  sx: number;
  sy: number;
  start: SLayout;
}

export const MIME_VISUAL = "application/x-mrp-visual";
export const MIME_FIELD = "application/x-mrp-field";

const TV_W = 1920;
const TV_H = 1080;

export const StudioCanvas = forwardRef<
  CanvasHandle,
  {
    reportId: string;
    source: "admin" | "owner";
    page: SPage;
    selectedId: string | null;
    /** Other widgets selected with Shift-click (selectedId is the primary one). */
    extraSelected?: string[];
    onSelect: (id: string | null) => void;
    onToggleSelect?: (id: string) => void;
    /** Drag a box on empty canvas to select everything it touches. */
    onSelectMany?: (ids: string[]) => void;
    /** Dragging a column border on a selected table: new widths as {column: "12.5%"}. */
    onColumnWidths?: (widgetId: string, widths: Record<string, string>) => void;
    /** Grid cells per standard cell (1 standard grid, 2 fine grid): scales the drop preview. */
    scale?: number;
    onLayout: (id: string, layout: SLayout) => void;
    onDropVisual: (type: string, at: { x: number; y: number }) => void;
    onDropField: (widgetId: string, field: import("./autoMeasure").FieldDrag) => void;
    onFramePage: (pageId: string) => void;
    onLoaded: () => void;
    /** Light/dark preview of the renderer (contract v2.1 section 1). */
    theme: PreviewTheme;
    /** Empty-page call-to-actions. */
    onEmptyAddVisual: () => void;
    onEmptySuggest: () => void;
    onEmptyTemplate: () => void;
    /** Guided empty state (Start here). */
    hasData?: boolean;
    onEmptyPickData?: () => void;
    onEmptyStyle?: () => void;
    /** A column dragged onto the empty part of the page: the designer picks the chart. */
    onDropFieldNew?: (field: import("./autoMeasure").FieldDrag, at: { x: number; y: number }) => void;
  }
>(function StudioCanvas({ reportId, source, page, selectedId, extraSelected = [], onToggleSelect, onSelectMany, onColumnWidths, scale: cellScale = 1, onSelect, onLayout, onDropVisual, onDropField, onFramePage, onLoaded, theme, onEmptyAddVisual, onEmptySuggest, onEmptyTemplate, hasData, onEmptyPickData, onEmptyStyle, onDropFieldNew }, ref) {
  const wrap = useRef<HTMLDivElement>(null);
  const iframe = useRef<HTMLIFrameElement>(null);
  const [geo, setGeo] = useState<{ scale: number; left: number; top: number } | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [ghost, setGhost] = useState<SLayout | null>(null);
  // Smart guides (magenta lines) while a drag is snapped to other visuals / the page margins.
  const [guides, setGuides] = useState<Guide[]>([]);
  const [marquee, setMarquee] = useState<{ sx: number; sy: number; cx: number; cy: number } | null>(null);
  const [dropCell, setDropCell] = useState<{ x: number; y: number } | null>(null);
  const [hoverField, setHoverField] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // v2.2 "Try it": the canvas becomes a live report -- dropdowns open,
  // charts filter on click, tooltips show -- while Ctrl is held or the
  // toggle is on. Editing (select/drag) is paused meanwhile.
  const [tryIt, setTryIt] = useState(false);
  const [ctrlDown, setCtrlDown] = useState(false);
  const interactive = tryIt || ctrlDown;
  const cb = useRef({ onFramePage, onLoaded });
  cb.current = { onFramePage, onLoaded };
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useImperativeHandle(ref, () => ({ post: (msg) => iframe.current?.contentWindow?.postMessage(msg, window.location.origin) }));

  const canvas = page.canvas ?? { width: GRID.width, height: GRID.height, columns: GRID.columns, rowHeight: GRID.rowHeight, gap: GRID.gap };
  const rows = rowsFor(page);
  // The preview frame is the TV screen (1920x1080) around the standard 1848x944 canvas. Any other page size
  // (A4, square, custom) gets a frame of ITS aspect ratio plus the same fixed header/margins, so the page is not squashed.
  const isTvCanvas = canvas.width === GRID.width && canvas.height === GRID.height;
  // adaptive=1: the renderer sizes its simulated screen to the page and reports it ({type:'mrp:layout'}); trust that
  // size over our estimate so the frame matches the renderer exactly (A4 is 900+ wide with a measured header).
  const [reported, setReported] = useState<{ w: number; h: number; pageId?: string } | null>(null);
  const useReported = !isTvCanvas && reported && reported.pageId === page.id;
  const frameW = isTvCanvas ? TV_W : useReported ? reported!.w : canvas.width + (TV_W - GRID.width);
  const frameH = isTvCanvas ? TV_H : useReported ? reported!.h : canvas.height + (TV_H - GRID.height);

  useEffect(() => {
    iframe.current?.contentWindow?.postMessage({ type: "mrp:selectPage", pageId: page.id }, window.location.origin);
  }, [page.id]);

  // Messages from the renderer frame.
  useEffect(() => {
    const h = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== iframe.current?.contentWindow) return;
      const m = e.data as { type?: string; pageId?: string; down?: boolean };
      if (m.type === "mrp:page" && m.pageId) cb.current.onFramePage(m.pageId);
      if (m.type === "mrp:ctrl") setCtrlDown(!!m.down);
      if (m.type === "mrp:layout") {
        const l = e.data as { width?: number; height?: number; pageId?: string };
        if (l.width && l.height) setReported({ w: l.width, h: l.height, pageId: l.pageId });
      }
      if (m.type === "mrp:rendered" || m.type === "mrp:loaded") {
        if (!loaded) {
          setLoaded(true);
          iframe.current?.contentWindow?.postMessage({ type: "mrp:theme", theme: themeRef.current }, window.location.origin);
          cb.current.onLoaded();
        }
      }
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  }, [loaded]);

  // Hold Ctrl (or Cmd) anywhere in the designer to use the report live.
  useEffect(() => {
    const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
    const down = (e: KeyboardEvent) => { if ((e.key === "Control" || e.key === "Meta") && !typing(e.target)) setCtrlDown(true); };
    const up = (e: KeyboardEvent) => { if (e.key === "Control" || e.key === "Meta") setCtrlDown(false); };
    const blur = () => setCtrlDown(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("blur", blur); };
  }, []);
  useEffect(() => {
    iframe.current?.contentWindow?.postMessage({ type: "mrp:interactive", on: interactive }, window.location.origin);
  }, [interactive, loaded]);

  // Live light/dark switch -- posted, never through the URL (that reloads the frame).
  useEffect(() => {
    iframe.current?.contentWindow?.postMessage({ type: "mrp:theme", theme }, window.location.origin);
  }, [theme]);

  // The renderer's own #stage rect is the ground truth for where cells are.
  useEffect(() => {
    let stop = false;
    const measure = () => {
      if (stop) return;
      const f = iframe.current;
      const w = wrap.current;
      const stage = f?.contentDocument?.getElementById("stage");
      if (f && w && stage) {
        const s = stage.getBoundingClientRect();
        const fr = f.getBoundingClientRect();
        const wr = w.getBoundingClientRect();
        if (s.width > 0) setGeo({ scale: s.width / canvas.width, left: fr.left + s.left - wr.left, top: fr.top + s.top - wr.top });
      }
    };
    measure();
    const t = window.setInterval(measure, 250);
    return () => {
      stop = true;
      window.clearInterval(t);
    };
  }, [canvas.width, page.id]);

  const scale = geo?.scale ?? 0.5;
  const colW = ((canvas.width - canvas.gap * (canvas.columns - 1)) / canvas.columns) * scale;
  const rowH = canvas.rowHeight * scale;
  const gap = canvas.gap * scale;
  const ox = geo?.left ?? 0;
  const oy = geo?.top ?? 0;
  const px = (l: SLayout) => ({ left: ox + l.x * (colW + gap), top: oy + l.y * (rowH + gap), width: l.w * colW + (l.w - 1) * gap, height: l.h * rowH + (l.h - 1) * gap });
  const cellAt = useCallback(
    (clientX: number, clientY: number) => {
      const r = wrap.current?.getBoundingClientRect();
      if (!r) return { x: 0, y: 0 };
      const x = Math.floor((clientX - r.left - ox) / (colW + gap));
      const y = Math.floor((clientY - r.top - oy) / (rowH + gap));
      return { x: Math.max(0, Math.min(canvas.columns - 1, x)), y: Math.max(0, y) };
    },
    [ox, oy, colW, rowH, gap, canvas.columns],
  );

  const startDrag = (e: ReactMouseEvent, w: SWidget, handle: Handle) => {
    e.stopPropagation();
    e.preventDefault();
    if ((e.shiftKey || e.ctrlKey || e.metaKey) && handle === "move" && onToggleSelect) {
      onToggleSelect(w.id);
      return;
    }
    if (handle !== "move" || (w.id !== selectedId && !extraSelected.includes(w.id))) onSelect(w.id);
    // A locked visual can be selected but not dragged or resized.
    if (w.locked) return;
    setDrag({ id: w.id, handle, sx: e.clientX, sy: e.clientY, start: w.layout });
    setGhost(w.layout);
  };

  useEffect(() => {
    if (!drag) return;
    // Snap targets: every grid visual that is not part of what is being dragged.
    const moving = new Set([drag.id, selectedId, ...extraSelected].filter(Boolean) as string[]);
    const others = page.widgets.filter((w) => !isBarWidget(w) && !moving.has(w.id)).map((w) => w.layout);
    const area = { cols: canvas.columns, rows };
    const move = (e: MouseEvent) => {
      const s = drag.start;
      // Alt turns snapping and guides off (plain whole-cell grid snapping stays).
      const snapOn = !e.altKey;
      const { dc, dr } = snapDelta({ start: s, handle: drag.handle, rawC: (e.clientX - drag.sx) / (colW + gap), rawR: (e.clientY - drag.sy) / (rowH + gap), others, area, threshold: 0.4 * cellScale, enabled: snapOn });
      let { x, y, w, h } = s;
      const hd = drag.handle;
      if (hd === "move") {
        x = s.x + dc;
        y = s.y + dr;
      } else {
        if (hd.includes("e")) w = s.w + dc;
        if (hd.includes("s")) h = s.h + dr;
        if (hd.includes("w")) {
          x = s.x + dc;
          w = s.w - dc;
        }
        if (hd.includes("n")) {
          y = s.y + dr;
          h = s.h - dr;
        }
      }
      w = Math.max(2, Math.min(canvas.columns, w));
      h = Math.max(2, h);
      x = Math.max(0, Math.min(canvas.columns - w, x));
      y = Math.max(0, y);
      setGhost({ ...s, x, y, w, h });
      const g = snapOn ? guidesFor({ x, y, w, h }, others, area) : [];
      setGuides((prev) => (JSON.stringify(prev) === JSON.stringify(g) ? prev : g));
    };
    const up = () => {
      setGuides([]);
      setGhost((g) => {
        if (g && (g.x !== drag.start.x || g.y !== drag.start.y || g.w !== drag.start.w || g.h !== drag.start.h)) onLayout(drag.id, g);
        // A plain click (no drag) on one of several selected visuals narrows the selection to it.
        else if (drag.handle === "move" && extraSelected.length > 0) onSelect(drag.id);
        return null;
      });
      setDrag(null);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [drag, colW, rowH, gap, canvas.columns, rows, cellScale, page.widgets, selectedId, onLayout, extraSelected, onSelect]);

  // ---- table column borders: drag to resize, like Excel / Power BI ----
  const [grips, setGrips] = useState<Array<{ i: number; x: number; top: number; h: number }>>([]);
  const [colDrag, setColDrag] = useState<{ i: number; sx: number; widths: number[]; names: string[]; guideX: number } | null>(null);
  const selectedWidget = page.widgets.find((w) => w.id === selectedId);
  const tableSelected = selectedWidget?.type === "table" ? selectedWidget : undefined;
  const columnNames = (w: SWidget): string[] =>
    [...(w.query?.dimensions ?? []), ...(w.query?.measures ?? [])].map((m: any) => (typeof m === "string" ? m : m?.name ?? m?.key ?? String(m)));
  useEffect(() => {
    if (!tableSelected || interactive) {
      setGrips([]);
      return;
    }
    const measure = () => {
      const f = iframe.current;
      const w = wrap.current;
      const ths = f?.contentDocument?.querySelectorAll(`[data-widget-id="${CSS.escape(tableSelected.id)}"] table th`);
      if (!f || !w || !ths || ths.length < 2) return setGrips([]);
      const fr = f.getBoundingClientRect();
      const wr = w.getBoundingClientRect();
      const next: Array<{ i: number; x: number; top: number; h: number }> = [];
      ths.forEach((th, i) => {
        if (i === ths.length - 1) return;
        const r = th.getBoundingClientRect();
        next.push({ i, x: fr.left - wr.left + r.right, top: fr.top - wr.top + r.top, h: r.height });
      });
      setGrips((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    measure();
    const t = window.setInterval(measure, 300);
    return () => window.clearInterval(t);
  }, [tableSelected?.id, interactive, page.id, geo?.scale]);

  const startColDrag = (e: ReactMouseEvent, i: number) => {
    e.stopPropagation();
    e.preventDefault();
    if (!tableSelected) return;
    const ths = iframe.current?.contentDocument?.querySelectorAll(`[data-widget-id="${CSS.escape(tableSelected.id)}"] table th`);
    if (!ths) return;
    const widths = Array.from(ths).map((th) => th.getBoundingClientRect().width);
    const names = columnNames(tableSelected);
    if (names.length !== widths.length) return;
    setColDrag({ i, sx: e.clientX, widths, names, guideX: grips.find((g) => g.i === i)?.x ?? 0 });
  };
  useEffect(() => {
    if (!colDrag) return;
    const frameScale = (() => {
      const f = iframe.current?.getBoundingClientRect();
      const doc = iframe.current?.contentDocument?.documentElement;
      return f && doc && doc.clientWidth ? f.width / doc.clientWidth : 1;
    })();
    const next = (e: MouseEvent) => {
      // widths are measured in the iframe's own pixels (already scaled by the page); move in screen pixels
      const dx = (e.clientX - colDrag.sx) / (frameScale || 1);
      const min = 36;
      const a = colDrag.widths[colDrag.i];
      const b = colDrag.widths[colDrag.i + 1];
      const d = Math.max(min - a, Math.min(b - min, dx));
      const widths = colDrag.widths.map((w, k) => (k === colDrag.i ? w + d : k === colDrag.i + 1 ? w - d : w));
      return { widths, d };
    };
    const move = (e: MouseEvent) => {
      const { d } = next(e);
      setColDrag((c) => (c ? { ...c, guideX: (grips.find((g) => g.i === c.i)?.x ?? 0) + d * frameScale } : c));
    };
    const up = (e: MouseEvent) => {
      const { widths } = next(e);
      const total = widths.reduce((s, w) => s + w, 0) || 1;
      const out: Record<string, string> = {};
      colDrag.names.forEach((n, k) => (out[n] = `${Math.round((widths[k] / total) * 1000) / 10}%`));
      const id = tableSelected?.id;
      setColDrag(null);
      if (id) onColumnWidths?.(id, out);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colDrag?.i, colDrag?.sx]);

  // Drag a box on empty canvas: select every visual it touches.
  useEffect(() => {
    if (!marquee) return;
    const rel = (e: MouseEvent) => {
      const r = wrap.current?.getBoundingClientRect();
      return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
    };
    const move = (e: MouseEvent) => {
      const p = rel(e);
      setMarquee((m) => (m ? { ...m, cx: p.x, cy: p.y } : m));
    };
    const up = (e: MouseEvent) => {
      const p = rel(e);
      const x0 = Math.min(marquee.sx, p.x), x1 = Math.max(marquee.sx, p.x);
      const y0 = Math.min(marquee.sy, p.y), y1 = Math.max(marquee.sy, p.y);
      setMarquee(null);
      if (x1 - x0 < 5 && y1 - y0 < 5) {
        onSelect(null);
        return;
      }
      const hit = page.widgets
        .filter((w) => !isBarWidget(w))
        .filter((w) => {
          const b = px(w.layout);
          return b.left < x1 && b.left + b.width > x0 && b.top < y1 && b.top + b.height > y0;
        })
        .map((w) => w.id);
      if (hit.length) onSelectMany?.(hit);
      else onSelect(null);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    // px() depends on geometry that is stable during a drag
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marquee?.sx, marquee?.sy, page, onSelect, onSelectMany]);

  const onDragOver = (e: DragEvent) => {
    if (e.dataTransfer.types.includes(MIME_VISUAL) || (onDropFieldNew && e.dataTransfer.types.includes(MIME_FIELD))) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setDropCell(cellAt(e.clientX, e.clientY));
    }
  };
  const onDrop = (e: DragEvent) => {
    const t = e.dataTransfer.getData(MIME_VISUAL);
    const rawField = onDropFieldNew ? e.dataTransfer.getData(MIME_FIELD) : "";
    setDropCell(null);
    if (t) {
      e.preventDefault();
      onDropVisual(t, cellAt(e.clientX, e.clientY));
    } else if (rawField) {
      e.preventDefault();
      onDropFieldNew?.(JSON.parse(rawField), cellAt(e.clientX, e.clientY));
    }
  };

  const gridWidgets = page.widgets.filter((w) => !isBarWidget(w));
  const ranks = stackRanks(page);
  const barWidgets = page.widgets.filter(isBarWidget);
  // Initial page only -- later switches are posted so the frame never reloads.
  const initialPage = useRef(page.id).current;
  const initialTheme = useRef(theme).current;
  const params = new URLSearchParams({ reportId, mode: source, embed: "1", design: "1", adaptive: "1", page: initialPage, theme: initialTheme });
  const empty = loaded && gridWidgets.length === 0;

  return (
    <div className="st-canvas-col">
      {barWidgets.length ? (
        <div className="st-filterbar" aria-label="Filter bar">
          <Filter size={14} className="ds-faint" />
          <span className="ds-small ds-faint">Filter bar:</span>
          {barWidgets.map((w) => (
            <button key={w.id} className={`st-chip${w.id === selectedId ? " is-selected" : ""}`} onClick={(e) => { e.stopPropagation(); onSelect(w.id); }}>
              {w.title || specFor(w.type)?.label || w.type}
            </button>
          ))}
        </div>
      ) : null}
      <div className="st-tryit-row">
        <button type="button" className={`st-tryit${interactive ? " is-on" : ""}`} aria-pressed={tryIt} onClick={() => setTryIt((v) => !v)} title="Use the report like a viewer: open dropdowns, click charts to filter. Or just hold Ctrl.">
          <MousePointerClick size={14} /> {interactive ? (tryIt ? "Trying it — click to go back to editing" : "Live while Ctrl is held") : "Try it (or hold Ctrl)"}
        </button>
      </div>
      <div
        ref={wrap}
        className={`st-canvas st-canvas--${theme}${interactive ? " is-interactive" : ""}`}
        style={{ aspectRatio: `${frameW} / ${frameH}`, ...(isTvCanvas ? {} : { maxWidth: `calc((100vh - 260px) * ${frameW / frameH})`, marginInline: "auto" }), background: PREVIEW_STAGE_BG[theme] }}
        onMouseDown={(e) => {
          if (interactive || !onSelectMany) return onSelect(null);
          const r = wrap.current?.getBoundingClientRect();
          setMarquee({ sx: e.clientX - (r?.left ?? 0), sy: e.clientY - (r?.top ?? 0), cx: e.clientX - (r?.left ?? 0), cy: e.clientY - (r?.top ?? 0) });
        }}
        onDragOver={onDragOver}
        onDragLeave={() => setDropCell(null)}
        onDrop={onDrop}
      >
        <iframe
          ref={iframe}
          key={`${reportId}-${source}`}
          title="Report canvas"
          src={`/tv-preview/preview.html?${params.toString()}`}
          className="st-frame"
          style={{ opacity: loaded ? 1 : 0 }}
        />
        {!loaded ? (
          <div className="st-loading" aria-live="polite">
            <div className="st-loading__sk" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => <span key={i} style={{ background: PREVIEW_SKELETON_BG[theme] }} />)}
              <span style={{ gridColumn: "span 3", background: PREVIEW_SKELETON_BG[theme] }} />
              <span style={{ background: PREVIEW_SKELETON_BG[theme] }} />
              <span style={{ gridColumn: "span 4", background: PREVIEW_SKELETON_BG[theme] }} />
            </div>
            <div className="st-loading__msg">
              <Loader2 size={16} className="ds-spin" />
              <span>
                <strong>Loading the canvas…</strong>
                <small>Drawing “{page.name}” with live data — the first open can take a few seconds.</small>
              </span>
            </div>
          </div>
        ) : null}
        {empty ? (
          <EmptyCanvas
            style={{ left: ox, top: oy, width: canvas.width * scale, height: rows * (rowH + gap) - gap }}
            gridSize={`${colW + gap}px ${rowH + gap}px`}
            theme={theme}
            dropping={!!dropCell}
            onAddVisual={onEmptyAddVisual}
            onSuggest={onEmptySuggest}
            onTemplate={onEmptyTemplate}
            hasData={hasData}
            onPickData={onEmptyPickData}
            onStyle={onEmptyStyle}
          />
        ) : null}
        {/* faint grid while dragging */}
        {(drag || dropCell) && !empty ? (
          <div className="st-grid" style={{ left: ox, top: oy, width: canvas.width * scale, height: rows * (rowH + gap) - gap, backgroundSize: `${colW + gap}px ${rowH + gap}px` }} />
        ) : null}
        {dropCell ? (
          <div className="st-drop" style={px({ x: Math.min(dropCell.x, canvas.columns - 6 * cellScale), y: dropCell.y, w: 6 * cellScale, h: 5 * cellScale })} />
        ) : null}
        {gridWidgets.map((w) => {
          const l = drag?.id === w.id && ghost ? ghost : w.layout;
          const p = px(l);
          const sel = w.id === selectedId;
          const multi = extraSelected.includes(w.id);
          const clash = drag?.id === w.id && ghost ? collides(page, w.id, ghost) : false;
          const outOfBounds = l.y + l.h > rows;
          const spec = specFor(w.type);
          return (
            <div
              key={w.id}
              className={`st-box${sel ? " is-selected" : ""}${multi ? " is-multi" : ""}${clash ? " is-clash" : ""}${hoverField === w.id ? " is-drop" : ""}${w.hidden ? " is-hidden" : ""}${w.locked ? " is-locked" : ""}`}
              // overlay stacking follows layout.z (rank) so overlapping visuals are picked top-most first
              style={{ ...p, zIndex: sel || drag?.id === w.id ? 1000 : 10 + (ranks.get(w.id) ?? 1) }}
              onMouseDown={(e) => startDrag(e, w, "move")}
              onDragOver={(e) => {
                if (e.dataTransfer.types.includes(MIME_FIELD)) {
                  e.preventDefault();
                  e.stopPropagation();
                  setHoverField(w.id);
                }
              }}
              onDragLeave={() => setHoverField((h) => (h === w.id ? null : h))}
              onDrop={(e) => {
                const raw = e.dataTransfer.getData(MIME_FIELD);
                setHoverField(null);
                if (raw) {
                  e.preventDefault();
                  e.stopPropagation();
                  onDropField(w.id, JSON.parse(raw));
                }
              }}
              title={`${w.title || spec?.label || w.type} — ${w.locked ? "locked (unlock in the Layers tab)" : "drag to move, drag edges to resize"}`}
            >
              {w.locked ? <span className="st-lockbadge" aria-label="Locked"><Lock size={11} /></span> : null}
              {sel || hoverField === w.id ? (
                <span className="st-tag">
                  <GripVertical size={11} />
                  {spec?.label ?? w.type}
                  {outOfBounds ? <AlertTriangle size={11} /> : null}
                </span>
              ) : null}
              {sel && !w.locked
                ? (["n", "s", "e", "w", "ne", "nw", "se", "sw"] as Handle[]).map((hd) => (
                    <span key={hd} className={`st-h st-h--${hd}`} onMouseDown={(e) => startDrag(e, w, hd)} />
                  ))
                : null}
            </div>
          );
        })}
        {onColumnWidths && tableSelected && !interactive
          ? grips.map((g) => (
              <span
                key={g.i}
                className="st-colgrip"
                title="Drag to resize this column"
                style={{ left: g.x - 5, top: g.top, height: Math.max(g.h, 16) }}
                onMouseDown={(e) => startColDrag(e, g.i)}
              />
            ))
          : null}
        {colDrag ? <span className="st-colguide" style={{ left: colDrag.guideX, top: grips[0]?.top ?? 0, height: (wrap.current?.clientHeight ?? 400) - (grips[0]?.top ?? 0) }} /> : null}
        {guides.map((g) => {
          const along = g.axis === "v" ? rowH + gap : colW + gap;
          const across = g.axis === "v" ? colW + gap : rowH + gap;
          const start = (g.axis === "v" ? oy : ox) + g.from * along - gap / 2;
          const len = (g.to - g.from) * along;
          return g.axis === "v" ? (
            <span key={`v${g.pos}`} className="st-guide st-guide--v" style={{ left: ox + g.pos * across - gap / 2, top: start, height: len }} />
          ) : (
            <span key={`h${g.pos}`} className="st-guide st-guide--h" style={{ top: oy + g.pos * across - gap / 2, left: start, width: len }} />
          );
        })}
        {marquee ? (
          <div
            className="st-marquee"
            style={{ left: Math.min(marquee.sx, marquee.cx), top: Math.min(marquee.sy, marquee.cy), width: Math.abs(marquee.cx - marquee.sx), height: Math.abs(marquee.cy - marquee.sy) }}
          />
        ) : null}
        {drag && ghost ? (
          <div className="st-coords" style={{ left: px(ghost).left, top: Math.max(0, px(ghost).top - 24) }}>
            col {ghost.x + 1}–{ghost.x + ghost.w} · row {ghost.y + 1}–{ghost.y + ghost.h}
          </div>
        ) : null}
      </div>
    </div>
  );
});
